import type { MetaFunction, LoaderFunctionArgs } from 'react-router';
import { useLoaderData } from 'react-router';
import WordGlory from '~/assets/word-glory.svg';
import { generateMeta } from '~/services/meta';
import { MicroLandingNew } from '~/components/ProductMicroLanding';

import type { SitemapFunction } from 'remix-sitemap';

const AI_PROVIDERS = {
    claude: {
        name: 'Claude',
        title: 'Claude plugin for Microsoft Word',
        subTitle:
            "Integrate Anthropic's Claude AI directly into Microsoft Word. Get thoughtful, nuanced writing assistance and content generation.",
        metaTitle: 'Claude Add-In for Microsoft Word',
        metaDescription:
            'Use Anthropic Claude AI in Microsoft Word. Thoughtful writing assistance, content generation, and document enhancement.',
        features: [
            "✅ Anthropic's Claude AI for thoughtful writing assistance",
            '📝 Nuanced understanding of context and tone',
            '🕒 Improve writing quality and save time',
            '🔁 Seamless integration within Word interface',
        ],
        feedback: {
            message:
                "Claude in Word provides the most thoughtful and context-aware writing suggestions. It really understands what we're trying to communicate.",
            name: 'David Park',
            role: 'Communications Director at Global Corp',
        },
    },
    gemini: {
        name: 'Gemini',
        title: 'Gemini plugin for Microsoft Word',
        subTitle:
            "Get Google's powerful Gemini AI directly in Microsoft Word. Improve your writing, generate content, and work smarter without switching apps.",
        metaTitle: 'Gemini Add-In for Microsoft Word',
        metaDescription:
            'Use Google Gemini AI in Microsoft Word. Improve writing, fix grammar, and generate content without leaving Word.',
        features: [
            "✅ Google's advanced Gemini AI integrated in Word",
            "🌍 Write better in any language with Gemini's multilingual capabilities",
            '🕒 Save hours every week with smart AI assistance',
            '🔁 Stay inside Word — no switching between apps',
        ],
        feedback: {
            message:
                'Having Gemini integrated directly in Word has transformed our writing workflow. The AI suggestions are incredibly accurate and help us create better content faster.',
            name: 'Michael Chen',
            role: 'Content Manager at TechCorp',
        },
    },
    deepseek: {
        name: 'DeepSeek',
        title: 'DeepSeek plugin for Microsoft Word',
        subTitle:
            "Experience DeepSeek's advanced AI capabilities directly in Microsoft Word. Enhance your writing with cutting-edge AI assistance.",
        metaTitle: 'DeepSeek Add-In for Microsoft Word',
        metaDescription:
            'Use DeepSeek AI in Microsoft Word. Advanced writing assistance, content generation, and document enhancement.',
        features: [
            "✅ DeepSeek's advanced AI integrated seamlessly",
            '🧠 Superior reasoning and content generation capabilities',
            '🕒 Boost productivity with intelligent writing assistance',
            '🔁 Work efficiently without leaving Word',
        ],
        feedback: {
            message:
                "DeepSeek in Word provides incredibly intelligent writing suggestions. It understands context better than other AI tools we've tried.",
            name: 'Sarah Williams',
            role: 'Research Writer at Innovation Labs',
        },
    },
    perplexity: {
        name: 'Perplexity',
        title: 'Perplexity plugin for Microsoft Word',
        subTitle:
            "Bring Perplexity's research-powered AI to Microsoft Word. Get accurate, well-sourced content and improve your writing with reliable AI assistance.",
        metaTitle: 'Perplexity Add-In for Microsoft Word',
        metaDescription:
            'Use Perplexity AI in Microsoft Word. Research-backed writing assistance with accurate, sourced content generation.',
        features: [
            '✅ Research-powered AI with accurate information',
            '🔍 Generate content backed by reliable sources',
            '🕒 Save research time with instant fact-checked content',
            '🔁 Stay focused in Word with integrated AI research',
        ],
        feedback: {
            message:
                'Perplexity in Word is perfect for our research team. The AI provides well-sourced information that we can trust for our academic writing.',
            name: 'Dr. Emily Rodriguez',
            role: 'Senior Researcher at Academic Institute',
        },
    },
};

export const sitemap: SitemapFunction = ({ config, request }) => {
    return Object.keys(AI_PROVIDERS).map((provider) => ({
        loc: `/plugin-for-word/${provider}`,
        lastmod: new Date().toISOString(),
        exclude: false,
    }));
};

export async function loader({ params }: LoaderFunctionArgs) {
    const integrationName = params.integrationName;

    if (
        !integrationName ||
        !AI_PROVIDERS[integrationName as keyof typeof AI_PROVIDERS]
    ) {
        throw new Response('Not Found', { status: 404 });
    }

    return {
        provider: AI_PROVIDERS[integrationName as keyof typeof AI_PROVIDERS],
    };
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
    if (!data?.provider) {
        return [{ title: 'Not Found' }];
    }

    return generateMeta({
        title: data.provider.metaTitle,
        description: data.provider.metaDescription,
    });
};

const instruction = {
    label: 'AI Perfect Assistant for Microsoft Word',
    videoId: '0a8a672363dd4d47864553ce010f9773',
    paddingTop: '72.5%',
    steps: [
        <>Highlight any part of the text in Microsoft Word document</>,
        <>
            Open AI Perfect Assistant, select any AI Prompt Template and Click
            "Generate with AI"
        </>,
        <>Click "Insert" to insert AI generated text to the Word document</>,
    ],
};

export const Demonstration = ({
    videoDefinition,
}: {
    videoDefinition: typeof instruction;
}) => {
    return (
        <div
            style={{
                position: 'relative',
                paddingTop: videoDefinition.paddingTop,
            }}>
            <iframe
                title={`${videoDefinition.label} - AI Perfect Assistant`}
                src={`https://customer-b5iz2r6cpeptxyuk.cloudflarestream.com/${videoDefinition.videoId}/iframe?muted=true&preload=true&loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-b5iz2r6cpeptxyuk.cloudflarestream.com%2F${videoDefinition.videoId}%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600&controls=true`}
                loading='lazy'
                style={{
                    border: 'none',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: '100%',
                }}
                className='rounded-2xl'
                allow='accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;'
                allowFullScreen
            />
        </div>
    );
};

export default function PluginWordPage() {
    const { provider } = useLoaderData<typeof loader>();

    return (
        <MicroLandingNew
            hero={{
                imgUrl: WordGlory,
                title: provider.title,
                subTitle: provider.subTitle,
                features: provider.features,
                samplePrompts: [
                    'fix-grammar',
                    'improve',
                    'paraphrase',
                    'summarize',
                    'translate',
                ],
                feedback: provider.feedback,
                install: [
                    {
                        label: 'Install for Word App',
                        url: '/chatgpt-for-microsoft-word/install/desktop',
                    },
                    {
                        label: 'Install for Word Online',
                        url: '/chatgpt-for-microsoft-word/install/online',
                    },
                ],
            }}
            instruction={{
                title: 'How it works inside Word?',
                demoEl: <Demonstration videoDefinition={instruction} />,
                steps: [
                    <>
                        <b>Open Word:</b> Highlight any part of the text in
                        Microsoft Word document
                    </>,
                    <>
                        <b>Use AI Perfect Assistant:</b> Open AI Perfect
                        Assistant, select any AI Prompt Template and Click
                        "Generate with AI"
                    </>,
                    <>
                        <b>Insert result to the document:</b> Click "Insert" to
                        insert AI generated text to the Word document
                    </>,
                ],
            }}
        />
    );
}
